// Shared style tokens/helpers — the single source of truth for Edifice
// View's visual language. Retheme (2026-09-10): square-cornered "blueprint"
// aesthetic — hairline borders over shadows, uppercase micro-labels, Barlow
// Condensed headings — while keeping every screen's existing layout intact.
// Reuses the real brand emerald as the accent system rather than importing
// a foreign color: `mint`/`mintDeep` are the same emerald pair the logo
// already uses (mintDeep was previously `accent`, mint was the previously-
// unused `accentLight`), just formalized into the new component language.
export const C = {
  ink: "#202B27",        // brand dark — header, reversed surfaces, base every neutral below is tinted from
  mint: "#69CBA8",        // bright accent — active-tab underline, positive-status dots, brand accent bar
  mintDeep: "#006B4F",    // hover/pressed states, primary button fill, stronger emphasis
  success: "#16A34A",     // "done/completed" — distinct from mint so it never reads as "this is the active tab"
  attention: "#D97706",   // "needs action" status (contracts expiring, past-SLA, coverage gaps)
  red: "#DC2626",         // destructive/error
  border: "#DDDFDC",      // hairline dividers — warm, ink-tinted (not cool slate-blue)
  bg: "#F4F4F2",          // page background — warm near-white
  muted: "#5B655F",       // secondary text — warm ink-tinted grey
  white: "#FFFFFF",
  tintAccent: "#EAF6F1",  // mint-tinted info background
  tintSuccess: "#F0FBF4",
  tintDanger: "#FEF2F2",
  tintWarning: "#FEF3C7",
  headerGhost: "#A8C0E8",       // ghost-button text/border on the dark header
  headerGhostBorder: "#3A5275",

  // Back-compat aliases — old call sites across the app still reference
  // these names; keep them pointed at the new equivalents rather than
  // doing a single giant rename in one pass (Phase B sweeps the app onto
  // the new names directly, file by file).
  navy: "#202B27",
  accent: "#006B4F",
  accentLight: "#69CBA8",
  green: "#006B4F",
  amber: "#D97706",
};

export const S = {
  page: { minHeight: "100vh", background: C.bg, fontFamily: "Barlow, sans-serif" },
  center: { minHeight: "100vh", background: C.ink, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  card: { background: C.white, borderRadius: 0, padding: "32px 28px", width: "100%", maxWidth: 380, border: `1px solid ${C.border}` },
  title: { fontSize: 20, fontWeight: 600, fontFamily: "'Barlow Condensed', sans-serif", color: C.ink, marginBottom: 4 },
  sub: { fontSize: 13, color: C.muted, marginBottom: 20 },
  input: { width: "100%", boxSizing: "border-box", padding: "11px 12px", borderRadius: 0, border: `1px solid ${C.border}`, marginBottom: 10, fontSize: 14, fontFamily: "inherit" },
  btn: (bg = C.mintDeep) => ({ width: "100%", padding: "12px 0", borderRadius: 0, border: "none", background: bg, color: C.white, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600, fontSize: 15, cursor: "pointer" }),
  ghostBtn: { width: "100%", padding: "10px 0", borderRadius: 0, border: `1px solid ${C.border}`, background: "transparent", color: C.muted, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600, fontSize: 14, cursor: "pointer" },
  err: { background: C.tintDanger, color: C.red, fontSize: 13, padding: "8px 12px", borderRadius: 0, marginBottom: 12 },
  header: { background: C.ink, color: C.white, padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  body: { padding: 24, maxWidth: 960, margin: "0 auto" },
  pill: (bg, color) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 999, background: bg, color, fontSize: 12, fontWeight: 700 }),
  // A bare "×" button is otherwise ~16px — pair with className="icon-tap-target"
  // (mobile.css) to widen the actual hit area to 44px without changing how
  // it looks, rather than inflating the visible glyph itself.
  iconBtn: { background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 16 },

  // — kicker: the uppercase, letter-spaced micro-label used for section
  // headers ("STAFF ACCESS —", table column heads, etc.) — previously
  // hand-written identically dozens of times across screens.
  kicker: { fontSize: 11, fontWeight: 700, fontFamily: "'Barlow Condensed', sans-serif", textTransform: "uppercase", letterSpacing: 0.8, color: C.muted },

  // — tag: a status/category chip (square, per the blueprint language),
  // distinct from `pill` which stays for numeric badges (unread counts).
  tag: (variant = "neutral") => {
    const variants = {
      neutral: { background: "#EAEAE8", color: C.ink },
      accent: { background: C.tintAccent, color: C.mintDeep },
      attention: { background: C.tintWarning, color: "#92400E" },
      outline: { background: "transparent", color: C.mintDeep, border: `1px solid ${C.mintDeep}` },
      success: { background: C.tintSuccess, color: C.success },
      danger: { background: C.tintDanger, color: C.red },
    };
    return { display: "inline-flex", alignItems: "center", fontSize: 11, letterSpacing: 0.2, padding: "3px 9px", borderRadius: 0, ...variants[variant] };
  },

  // — table: hairline-divider primitives for existing tabular UI (Scorecards,
  // Incidents, Workers lists, Reports) — restyles rows, doesn't add columns.
  tableHead: { textAlign: "left", fontSize: 10, fontWeight: 700, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.8, textTransform: "uppercase", color: C.muted, padding: "9px 12px", borderBottom: `1px solid ${C.border}` },
  tableCell: { padding: "11px 12px", fontSize: 13, borderBottom: `1px solid ${C.border}` },

  // — statStrip: the label-over-big-number block used in dashboard headers.
  statStrip: { display: "grid" },
  stat: { padding: "15px 22px", display: "flex", flexDirection: "column", gap: 3, borderLeft: `1px solid ${C.border}` },
  statLabel: { fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: C.muted },
  statValue: { fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600, fontSize: 34, lineHeight: 1 },
};
